/**
 * StorageArrayForm — Odoo-style full-screen detail view
 * Tabs: Identificación / Almacenamiento / Ubicación / Hardware / Licencia / Organización / Red / Alimentación
 * Supports both dedicated appliances and server-based storage (Linux, ZFS, Ceph, etc.)
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  SparklesIcon,
  MapPinIcon,
  CubeIcon,
  TagIcon,
  CircleStackIcon,
  DocumentDuplicateIcon,
  WrenchScrewdriverIcon,
  PencilSquareIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import Dialog from './common/Dialog';
import LocationTreeSelect from './common/LocationTreeSelect';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ServerDisksPanel from './ServerDisksPanel';
import ServerComponentsPanel from './ServerComponentsPanel';
import StorageLunsPanel from './StorageLunsPanel';
import ElementIpPanel from './ElementIpPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import NetworkConnectionsPanel from './NetworkConnectionsPanel';
import PowerConnectionsPanel from './PowerConnectionsPanel';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import DetailCrumb from './DetailCrumb';
import StorageFormSummary from './StorageFormSummary';

const PROTOCOLS = ['iSCSI', 'FC', 'NFS', 'SMB/CIFS', 'S3/Object', 'NVMe-oF'];

const PROTOCOL_COLORS = {
  iSCSI:      { bg: '#f0fdf4', color: '#15803d' },
  FC:         { bg: '#ede9fe', color: '#6d28d9' },
  NFS:        { bg: '#fef9c3', color: '#854d0e' },
  'SMB/CIFS': { bg: '#dbeafe', color: '#1e40af' },
  'S3/Object':{ bg: '#fff7ed', color: '#c2410c' },
  'NVMe-oF':  { bg: '#fce7f3', color: '#be185d' },
};

function StorageArrayForm({ storage, companyId, initialMode = 'create', onClose, onSave, onClone, cloningId, navItems, navIndex, onNavigate, variant = 'classic' }) {
  const isCards = variant === 'cards';
  const isExisting = Boolean(storage?.id);

  // Modo lectura por defecto en las fichas existentes (estándar del CMDB); el
  // alta y el botón «Editar» del listado piden `initialMode="edit"`.
  const [editing, setEditing] = useState(!isExisting || initialMode === 'edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isCloning = cloningId && storage?.id && cloningId === storage.id;

  // ── Data ──
  const [locations, setLocations] = useState([]);
  const [racks, setRacks] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);

  // ── Form ──
  const [form, setForm] = useState({
    name: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    asset_tag: '',
    hostname: '',
    ip_address: '',
    environment: 'prod',
    status: 'active',
    location_id: '',
    location_name: '',
    rack_id: '',
    rack_name: '',
    position_u: '',
    form_factor: 'rack',
    size_u: 2,
    role_ids: [],
    owner: '',
    license: '',
    license_status: 'vigente',
    license_expires_at: '',
    cpu: '',
    ram_gb: '',
    tags: '',
    // Storage-specific
    storage_flavor: 'appliance',
    os_distribution: '',
    protocols: [],
    raw_tb: '',
    usable_tb: '',
    cache_tb: '',
    disk_count: '',
    disk_type: '',
    disk_size_tb: '',
    raid_level: '',
    controller_count: '',
    ha_mode: false,
    management_url: '',
    disk_shelf_count: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  // Nombre confirmado: el título se edita en línea en la cabecera y solo cuenta
  // como cambio cuando se confirma con ✓ o Enter (ver FICHA_ESTANDAR_TARJETAS §3.2).
  const [committedName, setCommittedName] = useState(storage?.name || '');
  const nameDirty = isCards && isExisting && (form.name || '') !== (committedName || '');

  // ── UI ──
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [apiError, setApiError] = useState('');
  const [addingManufacturer, setAddingManufacturer] = useState(false);
  const [addingModel, setAddingModel] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState('');
  const [newModel, setNewModel] = useState('');

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty || nameDirty,
    saving,
    entity: 'esta cabina',
    actionLabel: 'pasas a otra cabina o cierras la ficha',
    save: () => handleSubmit(null, { force: true, keepEditing: editing }),
  });

  // ── Disk / Components panels ──
  const [showDisksPanel, setShowDisksPanel] = useState(false);
  const [diskCount, setDiskCount] = useState(0);
  const [lunCount, setLunCount] = useState(0);
  const [cabinetPorts, setCabinetPorts] = useState([]);
  const [showComponentsPanel, setShowComponentsPanel] = useState(false);
  const [componentsCount, setComponentsCount] = useState(0);
  const [hasBmcComponent, setHasBmcComponent] = useState(false);
  const [promotingBmc, setPromotingBmc] = useState(false);

  // Puertos físicos de la cabina (iSCSI-A/B, FC-x): cada camino de un LUN se
  // puede vincular a uno de ellos, que es el que sale cableado.
  const refreshCabinetPorts = useCallback(async () => {
    if (!isExisting || !storage?.id || !companyId) return;
    try {
      const res = await api.getComponents(storage.id, companyId);
      const rows = res.data || res.components || [];
      const ports = [];
      rows.forEach(comp => {
        (comp.ports || []).forEach(port => {
          ports.push({
            id: port.id,
            port_number: port.port_number,
            label: port.label,
            port_type: port.port_type,
            mac_address: port.mac_address,
            wwn: port.wwn,
            _component: comp.slot || comp.component_type,
          });
        });
      });
      setCabinetPorts(ports);
    } catch (_) { setCabinetPorts([]); }
  }, [isExisting, storage?.id, companyId]);

  const refreshLunCount = useCallback(async () => {
    if (!storage?.id || !companyId) return;
    try {
      const res = await api.getLuns(companyId, storage.id);
      setLunCount((res.data || []).length);
    } catch (_) { /* ignore */ }
  }, [storage?.id, companyId]);

  useEffect(() => { refreshCabinetPorts(); }, [refreshCabinetPorts]);
  useEffect(() => { refreshLunCount(); }, [refreshLunCount]);
  const [ipCount, setIpCount] = useState(0);
  const [accessCount, setAccessCount] = useState(0);

  // ── Load disk count ──
  useEffect(() => {
    if (!storage?.id) return;
    api.getStorageArrayDiskInventory(storage.id, companyId)
      .then(r => {
        const disks = r.data?.data?.physical_disks;
        setDiskCount(Array.isArray(disks) ? disks.length : 0);
      })
      .catch(() => {});
  }, [storage?.id, companyId]);

  const refreshComponentsCount = async () => {
    if (!storage?.id || !companyId) return;
    try {
      const res = await api.getComponents(storage.id, companyId);
      const comps = Array.isArray(res?.data) ? res.data : [];
      setComponentsCount(comps.length);
      setHasBmcComponent(comps.some(c => c.component_type === 'bmc'));
    } catch {
      setComponentsCount(0);
      setHasBmcComponent(false);
    }
  };

  useEffect(() => {
    refreshComponentsCount();
  }, [storage?.id, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load supporting data ──
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    api.getRacks(null, companyId).then(r => setRacks(r.data || [])).catch(() => {});
    api.getRoles(companyId, { objectType: 'storage', includeGlobal: true })
      .then(r => setRoleOptions(r.data || [])).catch(() => {});
  }, [companyId]);

  const loadCatalogs = useCallback(async () => {
    if (!companyId) { setManufacturerOptions([]); setModelOptions([]); return; }
    try {
      const [mfrRes, modelRes] = await Promise.all([
        api.getManufacturers(companyId, { deviceType: 'storage' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, { deviceType: 'storage' }).catch(() => ({ data: [] })),
      ]);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modelRes?.data) ? modelRes.data : []);
    } catch (_) {}
  }, [companyId]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  // ── Populate form from prop ──
  useEffect(() => {
    if (!storage) return;
    setForm(prev => ({
      ...prev,
      ...storage,
      location_id: storage.location_id || prev.location_id || '',
      rack_id: storage.rack_id || '',
      rack_name: storage.rack_name || '',
      license_status: storage.license_status || 'vigente',
      environment: storage.environment || 'prod',
      status: storage.status || 'active',
      form_factor: storage.form_factor || 'rack',
      position_u: storage.position_u || storage.position || '',
      size_u: storage.size_u || 2,
      tags: Array.isArray(storage.tags) ? storage.tags.join(', ') : (storage.tags || ''),
      role_ids: Array.isArray(storage.roles)
        ? storage.roles.map(r => r.id).filter(Boolean)
        : (storage.role_ids || []),
      protocols: Array.isArray(storage.protocols) ? storage.protocols : [],
      ha_mode: Boolean(storage.ha_mode),
      storage_flavor: storage.storage_flavor || 'appliance',
    }));
    setCommittedName(storage.name || '');
  }, [storage]);

  // ── Derived ──
  const filteredRacks = useMemo(() => {
    if (!form.location_id) return racks;
    return racks.filter(r => String(r.location_id) === String(form.location_id));
  }, [racks, form.location_id]);

  const selectedRack = useMemo(() =>
    filteredRacks.find(r => String(r.id) === String(form.rack_id)) ||
    racks.find(r => String(r.id) === String(form.rack_id)),
    [filteredRacks, racks, form.rack_id]
  );

  const rackHeight = selectedRack?.height_u ? Number(selectedRack.height_u) : null;
  const sizeVal = Number(form.size_u || 0);
  const positionVal = form.position_u ? Number(form.position_u) : null;
  const bottomU = positionVal && sizeVal ? positionVal - sizeVal + 1 : null;
  const rangeLabel = positionVal && sizeVal
    ? (sizeVal === 1 ? `U${positionVal}` : `U${positionVal} – U${bottomU}`)
    : null;

  const selectedRoles = useMemo(() =>
    (form.role_ids || []).map(rid => roleOptions.find(r => Number(r.id) === Number(rid))).filter(Boolean),
    [form.role_ids, roleOptions]
  );

  const statusColors = {
    active:      { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
    maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
    decom:       { bg: '#fee2e2', color: '#991b1b', label: 'Decom' },
  };
  const currentStatus = statusColors[form.status] || statusColors.active;

  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.manufacturer]);

  const capacitySummary = [
    form.raw_tb    ? `${form.raw_tb} TB bruto`    : null,
    form.usable_tb ? `${form.usable_tb} TB usable` : null,
  ].filter(Boolean).join(' · ');

  // ── Handlers ──
  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setApiError('');
    setErrors([]);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  /* Título editable en línea (FICHA_ESTANDAR_TARJETAS §3.2): Enter confirma y
     guarda; Esc devuelve el valor original. */
  const commitTitle = async () => {
    if (!nameDirty && !isDirty) return;
    await handleSubmit(null, { force: true });
  };

  const restoreTitle = () => {
    setForm(prev => ({ ...prev, name: committedName }));
    if (!editing) setIsDirty(false);
  };

  const irACabina = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setErrors([]);
      setApiError('');
      onNavigate?.(destino);
    });
  };

  const toggleProtocol = (proto) => {
    setForm(prev => {
      const current = Array.isArray(prev.protocols) ? prev.protocols : [];
      const next = current.includes(proto)
        ? current.filter(p => p !== proto)
        : [...current, proto];
      return { ...prev, protocols: next };
    });
    setIsDirty(true);
  };

  const handleLocationChange = async e => {
    const value = e.target.value;
    const loc = locations.find(l => String(l.id) === value);
    setForm(prev => ({
      ...prev,
      location_id: value,
      location_name: loc?.name || '',
      rack_id: '',
      rack_name: '',
    }));
    setIsDirty(true);
    try {
      const r = await api.getRacks(value || null, companyId);
      setRacks(r.data || []);
    } catch (_) {}
  };

  const handleRackChange = e => {
    const value = e.target.value;
    const rack = racks.find(r => String(r.id) === value);
    setForm(prev => ({
      ...prev,
      rack_id: value,
      rack_name: rack?.name || rack?.code || '',
      location_id: rack?.location_id ? String(rack.location_id) : prev.location_id,
      location_name: rack?.location_name || prev.location_name,
    }));
    setIsDirty(true);
  };

  /* Cambiar de fabricante deja el modelo anterior sin sentido (§12.C14). */
  const handleManufacturerChange = (value) => {
    const antes = form.manufacturer || '';
    setField('manufacturer', value);
    if (antes !== (value || '') && form.model) setField('model', '');
  };

  const handleRolesChange = e => {
    const ids = Array.from(e.target.selectedOptions || [])
      .map(opt => Number(opt.value)).filter(Boolean);
    setField('role_ids', ids);
  };

  const handleDiscard = () => {
    if (storage) setForm(prev => ({
      ...prev, ...storage,
      tags: Array.isArray(storage.tags) ? storage.tags.join(', ') : (storage.tags || ''),
      role_ids: Array.isArray(storage.roles) ? storage.roles.map(r => r.id).filter(Boolean) : (storage.role_ids || []),
      protocols: Array.isArray(storage.protocols) ? storage.protocols : [],
    }));
    else onClose();
    setIsDirty(false);
    setErrors([]);
    setApiError('');
    // Descartar en una ficha ya guardada vuelve a LECTURA (no la cierra).
    if (isExisting) setEditing(false);
  };

  const handleSubmit = async (e, { force = false, keepEditing = false } = {}) => {
    if (e) e.preventDefault();
    // `force` permite guardar solo el título desde la cabecera aunque el cuerpo
    // siga en lectura.
    if (!editing && !(force && (isDirty || nameDirty))) return false;
    setSaving(true);
    setApiError('');
    try {
      const errs = [];
      if (!form.name) errs.push('El nombre es obligatorio');
      if (!form.location_id) errs.push('La ubicación es obligatoria');
      if (Number(form.size_u) < 1) errs.push('Las U deben ser >= 1');
      if (errs.length) { setErrors(errs); setSaving(false); return false; }

      const loc = locations.find(l => String(l.id) === String(form.location_id));
      const rack = racks.find(r => String(r.id) === String(form.rack_id));

      const payload = {
        ...form,
        location_id: form.location_id ? Number(form.location_id) : null,
        rack_id: form.rack_id ? Number(form.rack_id) : null,
        position_u: form.position_u ? Number(form.position_u) : null,
        size_u: Number(form.size_u || 2),
        ram_gb: form.ram_gb ? Number(form.ram_gb) : undefined,
        raw_tb: form.raw_tb !== '' ? Number(form.raw_tb) : undefined,
        usable_tb: form.usable_tb !== '' ? Number(form.usable_tb) : undefined,
        cache_tb: form.cache_tb !== '' ? Number(form.cache_tb) : undefined,
        disk_count: form.disk_count !== '' ? Number(form.disk_count) : undefined,
        disk_size_tb: form.disk_size_tb !== '' ? Number(form.disk_size_tb) : undefined,
        controller_count: form.controller_count !== '' ? Number(form.controller_count) : undefined,
        disk_shelf_count: form.disk_shelf_count !== '' ? Number(form.disk_shelf_count) : undefined,
        location_name: loc?.name || form.location_name,
        rack_name: rack?.name || rack?.code || form.rack_name,
        tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
        role_ids: form.role_ids || [],
        protocols: Array.isArray(form.protocols) ? form.protocols : [],
      };

      const resp = isExisting
        ? await api.updateStorageArray(storage.id, payload, companyId)
        : await api.createStorageArray(payload, companyId);

      if (!resp?.success && resp?.message) throw new Error(resp.message);
      setIsDirty(false);
      setCommittedName(form.name || '');
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(isExisting ? null : registroGuardado(resp));
      // Una ficha existente se queda abierta EN LECTURA tras guardar (salvo que
      // se esté pasando a la siguiente con «Guardar y continuar»).
      if (isExisting && !keepEditing) setEditing(false);
      return true;
    } catch (err) {
      setApiError(err.message || 'Error guardando cabina de almacenamiento');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await api.deleteStorageArray(storage.id, companyId);
      onClose?.();
    } catch (err) {
      setApiError(err.message || 'Error eliminando cabina');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToServer = async () => {
    if (!isExisting || !storage?.id) return;
    if (!window.confirm('¿Convertir esta cabina en servidor? Pasará a aparecer en el módulo de Servidores.')) return;
    setSaving(true);
    setApiError('');
    try {
      await api.changeElementType(storage.id, 'server', companyId);
      onSave?.();
      onClose?.();
    } catch (err) {
      setApiError(err.message || 'No se pudo convertir el elemento');
    } finally {
      setSaving(false);
    }
  };

  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({
        type: 'storage', companyId,
        locationId: form.location_id || undefined,
        rackId: form.rack_id || undefined,
        roleIds: form.role_ids || [],
        consume: true,
      });
      const s = resp?.data?.suggested_name;
      if (s) setForm(prev => ({ ...prev, name: s, hostname: prev.hostname || s }));
      setIsDirty(true);
    } catch (_) {}
    finally { setSuggesting(false); }
  };

  const saveManufacturerToCatalog = async () => {
    const name = newManufacturer || form.manufacturer;
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: 'storage' }, companyId);
      await loadCatalogs();
      setAddingManufacturer(false);
      setNewManufacturer('');
    } catch (e) { setApiError(e.message || 'No se pudo guardar fabricante'); }
  };

  /* Alta directa desde el `+` de las tarjetas: crea el valor en el catálogo. */
  const crearFabricante = async (name) => {
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: 'storage' }, companyId);
      await loadCatalogs();
    } catch (e) { setApiError(e.message || 'No se pudo guardar fabricante'); }
  };

  const crearModelo = async (name) => {
    if (!name || !companyId) return;
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name, device_type: 'storage', manufacturer_id: matchedMfr?.id }, companyId);
      await loadCatalogs();
    } catch (e) { setApiError(e.message || 'No se pudo guardar modelo'); }
  };

  const saveModelToCatalog = async () => {
    const modelName = newModel || form.model;
    if (!modelName || !companyId) return;
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name: modelName, device_type: 'storage', manufacturer_id: matchedMfr?.id }, companyId);
      await loadCatalogs();
      setAddingModel(false);
      setNewModel('');
    } catch (e) { setApiError(e.message || 'No se pudo guardar modelo'); }
  };

  const tabs = [
    { key: 'general',      label: 'Identificación' },
    { key: 'ubicacion',    label: 'Ubicación' },
    { key: 'hardware',     label: 'Hardware' },
    ...(isExisting ? [{ key: 'luns', label: `LUNs (${lunCount})` }] : []),
    { key: 'licencia',     label: 'Licencia' },
    { key: 'organizacion', label: `Organización${selectedRoles.length > 0 ? ` (${selectedRoles.length})` : ''}` },
    { key: 'access',       label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
    { key: 'red',          label: `Red / IP${ipCount > 0 ? ` (${ipCount})` : ''}` },
    { key: 'power',        label: '⚡ Alimentación' },
  ];

  // En tarjetas, Identificación y Ubicación se editan en Resumen.
  const tabsS = isCards
    ? [
        { key: 'general',      label: 'General' },
        { key: 'hardware',     label: 'Hardware' },
        ...(isExisting ? [{ key: 'luns', label: `LUNs (${lunCount})` }] : []),
        { key: 'licencia',     label: 'Licencia' },
        { key: 'organizacion', label: `Organización${selectedRoles.length > 0 ? ` (${selectedRoles.length})` : ''}` },
        { key: 'access',       label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
        { key: 'red',          label: `Red / IP${ipCount > 0 ? ` (${ipCount})` : ''}` },
        { key: 'power',        label: 'Alimentación' },
      ]
    : tabs;
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;
  const tabsNav = (
    <div className="dv-tabs-nav">
      {tabsS.map(tab => (
        <button
          key={tab.key}
          type="button"
          className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
          onClick={() => setActiveTab(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  // ── Disk inventory panel ──
  if (showDisksPanel) {
    return (
      <ServerDisksPanel
        server={storage}
        companyId={companyId}
        getStorage={(id, cid) => api.getStorageArrayDiskInventory(id, cid)}
        updateStorage={(id, data, cid) => api.updateStorageArrayDiskInventory(id, data, cid)}
        onClose={(newCount) => {
          setShowDisksPanel(false);
          if (typeof newCount === 'number') setDiskCount(newCount);
        }}
      />
    );
  }

  // ── Components panel ──
  if (showComponentsPanel) {
    return (
      <ServerComponentsPanel
        server={storage}
        companyId={companyId}
        getStorage={(id, cid) => api.getStorageArrayDiskInventory(id, cid)}
        updateStorage={(id, data, cid) => api.updateStorageArrayDiskInventory(id, data, cid)}
        onClose={() => { setShowComponentsPanel(false); refreshComponentsCount(); }}
        onUpdated={refreshComponentsCount}
      />
    );
  }

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Cabinas
        </button>
        <DetailCrumb label={form.name} />

        {/* ── Navegación entre fichas: siempre a la izquierda del spacer ── */}
        <FormPager
          items={navItems || []}
          index={navIndex}
          label="Cabina"
          disabled={saving}
          onChange={irACabina}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />

        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {(apiError || errors.length > 0) && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError || errors[0]}
          </span>
        )}

        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              {!editing && isExisting && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving} title="Editar la ficha">
                  <PencilSquareIcon />
                  Editar
                </button>
              )}
              {onClone && isExisting && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => onClone(storage)} disabled={saving || isCloning}>
                  {isCloning ? <span className="dv-spinner" /> : <DocumentDuplicateIcon />}
                  {isCloning ? 'Copiando…' : 'Copiar'}
                </button>
              )}
              {isExisting && (
                <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
                  <TrashIcon />
                </button>
              )}
            </>
          ) : (
          <>
          <span className="dv-status-badge" style={{ background: currentStatus.bg, color: currentStatus.color }}>
            ● {currentStatus.label}
          </span>
          {storage?.has_agent && (
            <span
              className="dv-status-badge"
              style={{
                background: storage.agent_status === 'online' ? 'rgba(22,163,74,.15)' : 'rgba(148,163,184,.18)',
                color: storage.agent_status === 'online' ? '#16a34a' : '#64748b',
              }}
              title={`Agente ${storage.agent_status || 'desconocido'}${storage.last_seen_at ? ` \u00b7 \u00faltimo contacto ${storage.last_seen_at}` : ''}`}
            >
              🤖 Agente {storage.agent_status || '—'}
            </span>
          )}

          {/* En LECTURA solo se ofrece pasar a edición (y las acciones de
              copia/borrado); en EDICIÓN, descartar y guardar. */}
          {!editing && isExisting && (
            <>
              {onClone && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => onClone(storage)} disabled={saving || isCloning}>
                  {isCloning ? <span className="dv-spinner" /> : <DocumentDuplicateIcon />}
                  {isCloning ? 'Copiando…' : 'Copiar'}
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving}>
                <PencilSquareIcon />
                Editar
              </button>
            </>
          )}

          {editing && (
            <>
              {isExisting && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
                  <XMarkIcon />
                  Descartar
                </button>
              )}
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={handleSubmit}
                disabled={saving || (isExisting && !isDirty)}
              >
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}

          {isExisting && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={handleConvertToServer}
              disabled={saving}
              title="Reclasificar esta cabina como servidor"
            >
              <CubeIcon />
              A servidor
            </button>
          )}
          {isExisting && (
            <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
              <TrashIcon />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')} title="Ir a Ubicación">
          <span className="dv-smart-btn-icon"><MapPinIcon style={{ width: 18, height: 18, color: '#0369a1' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#0369a1', fontSize: '0.85rem' }}>
            {form.location_name || (form.location_id ? '—' : 'Sin ubicación')}
          </span>
          <span className="dv-smart-btn-label">Ubicación</span>
        </button>

        {form.rack_id && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')} title="Rack asignado">
            <span className="dv-smart-btn-icon"><CubeIcon style={{ width: 18, height: 18, color: '#7c3aed' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.85rem' }}>
              {form.rack_name || 'Rack'}
              {rangeLabel && <span style={{ fontWeight: 400, fontSize: '0.72rem' }}> {rangeLabel}</span>}
            </span>
            <span className="dv-smart-btn-label">Rack</span>
          </button>
        )}

        {capacitySummary && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('hardware')} title="Capacidad">
            <span className="dv-smart-btn-icon"><CircleStackIcon style={{ width: 18, height: 18, color: '#0891b2' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#0891b2', fontSize: '0.85rem' }}>{capacitySummary}</span>
            <span className="dv-smart-btn-label">Capacidad</span>
          </button>
        )}

        {isExisting && (
          <button className="dv-smart-btn" type="button" onClick={() => setShowDisksPanel(true)} title="Inventario de discos">
            <span className="dv-smart-btn-icon"><CircleStackIcon style={{ width: 18, height: 18, color: '#7c3aed' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.85rem' }}>
              {diskCount > 0 ? `${diskCount} discos` : 'Ver discos'}
            </span>
            <span className="dv-smart-btn-label">Inventario</span>
          </button>
        )}

        {isExisting && (
          <button className="dv-smart-btn" type="button" onClick={() => setShowComponentsPanel(true)} title="Componentes HBA / NIC">
            <span className="dv-smart-btn-icon"><WrenchScrewdriverIcon style={{ width: 18, height: 18, color: '#059669' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#059669', fontSize: '0.85rem' }}>
              {componentsCount > 0 ? `${componentsCount} comps.` : 'HBA / NIC'}
            </span>
            <span className="dv-smart-btn-label">Componentes</span>
          </button>
        )}

        {selectedRoles.length > 0 && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('organizacion')} title="Roles">
            <span className="dv-smart-btn-icon"><TagIcon style={{ width: 18, height: 18, color: '#d97706' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#d97706', fontSize: '0.85rem' }}>
              {selectedRoles.map(r => r.code || r.name).join(', ')}
            </span>
            <span className="dv-smart-btn-label">Roles</span>
          </button>
        )}

        {isExisting && (
          <DocSmartButton elementType="storage" elementId={storage.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Tabs (solo en la ficha clásica: en tarjetas van en la cabecera) ── */}
      {!isCards && tabsNav}

      {/* ── Tab Content ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<CircleStackIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => { setForm(f => ({ ...f, name: v })); setIsDirty(true); }}
                dirty={isDirty}
                titleDirty={nameDirty}
                onTitleCommit={commitTitle}
                onTitleReset={restoreTitle}
                placeholder="Nombre de la cabina…"
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                crumb={[form.manufacturer, form.model, form.location_name, form.rack_name].filter(Boolean).join(' › ')}
                badges={[
                  { text: currentStatus.label, color: form.status === 'active' ? '#16a34a' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                  ...(storage?.has_agent ? [{ text: `Agente ${storage.agent_status || '—'}`, color: storage.agent_status === 'online' ? '#16a34a' : '#94a3b8' }] : []),
                  ...(capacitySummary ? [{ text: capacitySummary }] : []),
                ]}
                chips={[
                  ...(form.rack_name ? [{ value: form.rack_name, label: 'rack' }] : []),
                  { value: diskCount, label: 'discos', onClick: () => setShowDisksPanel(true) },
                  { value: componentsCount, label: 'componentes', onClick: () => setShowComponentsPanel(true) },
                  ...(isExisting ? [{ value: lunCount, label: 'LUNs', onClick: () => setActiveTab('luns') }] : []),
                  ...(selectedRoles.length ? [{ value: selectedRoles.length, label: 'roles', onClick: () => setActiveTab('organizacion') }] : []),
                ]}
                extra={isExisting ? (
                  <DocSmartButton elementType="storage" elementId={storage.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>{/* la ficha clásica no tiene título aparte: el nombre va en Identificación */}</>
            )}
            tabs={isCards ? tabsNav : null}
          >
          {/* En LECTURA el fieldset desactiva de golpe todos los campos,
              selects, textareas y botones internos (nativo de HTML). */}
          <fieldset disabled={!editing} className="dv-fieldset">

          {/* ─── GENERAL EN TARJETAS (dos columnas) ─── */}
          {isCards && activeTab === 'general' && (
            <div className={paneClass}>
              <StorageFormSummary
                form={form}
                editing={editing}
                onChange={handleChange}
                onNameSuggest={handleSuggest}
                suggesting={suggesting}
                manufacturerOptions={manufacturerOptions}
                modelOptions={filteredModelOptions}
                onManufacturerChange={handleManufacturerChange}
                onCreateManufacturer={async (name) => {
                  handleManufacturerChange(name);
                  await crearFabricante(name);
                }}
                onCreateModel={async (name) => {
                  setField('model', name);
                  await crearModelo(name);
                }}
                locations={locations}
                racks={filteredRacks}
                rackName={selectedRack?.name || form.rack_name || ''}
                onLocationChange={handleLocationChange}
                onRackChange={handleRackChange}
                rangeLabel={rangeLabel}
                rackHeight={rackHeight}
                hasBmcComponent={hasBmcComponent}
                canPromoteBmc={isExisting}
                promotingBmc={promotingBmc}
                onPromoteBmc={async () => {
                  setPromotingBmc(true);
                  try {
                    await api.promoteBmc(storage.id, companyId);
                    await refreshComponentsCount();
                  } catch (e) {
                    setApiError(e.message || 'No se pudo crear la interfaz de gestión');
                  } finally {
                    setPromotingBmc(false);
                  }
                }}
                protocols={PROTOCOLS}
                protocolColors={PROTOCOL_COLORS}
                onToggleProtocol={toggleProtocol}
              />
            </div>
          )}

          {/* ─── IDENTIFICACIÓN (ficha clásica) ─── */}
          {!isCards && activeTab === 'general' && (
            <div className={paneClass}>
            <div className="dv-section">
              <div className="dv-section-title">Identificación</div>
              <div className="dv-grid-2">
                {/* Nombre */}
                <div className="dv-field">
                  <label className="dv-label">Nombre *</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      className="dv-input"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="SAN-0001"
                    />
                    <button
                      type="button"
                      className="dv-btn dv-btn-secondary"
                      onClick={handleSuggest}
                      disabled={suggesting}
                      title="Sugerir nombre automático"
                      style={{ flexShrink: 0 }}
                    >
                      {suggesting ? <span className="dv-spinner" /> : <SparklesIcon style={{ width: 16, height: 16 }} />}
                    </button>
                  </div>
                </div>

                {/* Hostname */}
                <div className="dv-field">
                  <label className="dv-label">Hostname</label>
                  <input className="dv-input" name="hostname" value={form.hostname} onChange={handleChange} placeholder="san-01.empresa.local" />
                </div>

                {/* IP de gestión */}
                <div className="dv-field">
                  <label className="dv-label" title="IP de gestión fuera de banda (SP del controlador, MGMT, iLO…)">IP de Gestión</label>
                  <input className="dv-input" name="ip_address" value={form.ip_address} onChange={handleChange} placeholder="10.0.0.10" />
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {hasBmcComponent ? (
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>✔ Interfaz de gestión (BMC/iLO) creada · cableable en Canvas</span>
                    ) : storage?.id ? (
                      <button
                        type="button"
                        disabled={promotingBmc}
                        onClick={async () => {
                          setPromotingBmc(true);
                          try {
                            await api.promoteBmc(storage.id, companyId);
                            await refreshComponentsCount();
                          } catch (e) {
                            alert('Error: ' + e.message);
                          } finally {
                            setPromotingBmc(false);
                          }
                        }}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6366f1', fontSize: '0.7rem', fontWeight: 600, textDecoration: 'underline' }}
                      >
                        {promotingBmc ? 'Creando…' : '🔌 Crear interfaz cableable (BMC / iLO)'}
                      </button>
                    ) : (
                      <span>Campo manual · guarda primero para crear la interfaz</span>
                    )}
                  </div>
                </div>

                {/* URL de gestión */}
                <div className="dv-field">
                  <label className="dv-label">URL de Gestión</label>
                  <input className="dv-input" name="management_url" value={form.management_url} onChange={handleChange} placeholder="https://san-01:8443" />
                </div>

                {/* Número de serie */}
                <div className="dv-field">
                  <label className="dv-label">Número de serie</label>
                  <input className="dv-input" name="serial_number" value={form.serial_number} onChange={handleChange} />
                </div>

                {/* Asset tag */}
                <div className="dv-field">
                  <label className="dv-label">Asset tag</label>
                  <input className="dv-input" name="asset_tag" value={form.asset_tag} onChange={handleChange} />
                </div>

                {/* Estado */}
                <div className="dv-field">
                  <label className="dv-label">Estado</label>
                  <select className="dv-select" name="status" value={form.status} onChange={handleChange}>
                    <option value="active">Activo</option>
                    <option value="maintenance">Mantenimiento</option>
                    <option value="decom">Decom</option>
                  </select>
                </div>

                {/* Entorno */}
                <div className="dv-field">
                  <label className="dv-label">Entorno</label>
                  <select className="dv-select" name="environment" value={form.environment} onChange={handleChange}>
                    <option value="prod">🟢 Producción</option>
                    <option value="pre">🟡 Pre-producción</option>
                    <option value="dev">🔵 Desarrollo</option>
                  </select>
                </div>

                {/* Fabricante */}
                <div className="dv-field">
                  <label className="dv-label">Fabricante</label>
                  {!addingManufacturer ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        className="dv-input"
                        name="manufacturer"
                        value={form.manufacturer}
                        onChange={e => handleManufacturerChange(e.target.value)}
                        list="mfr-list-storage"
                        placeholder="HPE, NetApp, Pure Storage…"
                      />
                      <datalist id="mfr-list-storage">
                        {manufacturerOptions.map(m => <option key={m.id} value={m.name} />)}
                      </datalist>
                      {form.manufacturer && !manufacturerOptions.some(m => m.name?.toLowerCase() === form.manufacturer?.toLowerCase()) && (
                        <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setAddingManufacturer(true)} style={{ flexShrink: 0, fontSize: '0.75rem' }}>+Catálogo</button>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="dv-input" value={newManufacturer || form.manufacturer} onChange={e => setNewManufacturer(e.target.value)} placeholder="Nombre fabricante" />
                      <button type="button" className="dv-btn dv-btn-primary" onClick={saveManufacturerToCatalog} style={{ flexShrink: 0 }}>Guardar</button>
                      <button type="button" className="dv-btn dv-btn-secondary" onClick={() => { setAddingManufacturer(false); setNewManufacturer(''); }} style={{ flexShrink: 0 }}>✕</button>
                    </div>
                  )}
                </div>

                {/* Modelo */}
                <div className="dv-field">
                  <label className="dv-label">Modelo</label>
                  {!addingModel ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        className="dv-input"
                        name="model"
                        value={form.model}
                        onChange={handleChange}
                        list="model-list-storage"
                        placeholder="MSA 2062, AFF A250…"
                      />
                      <datalist id="model-list-storage">
                        {filteredModelOptions.map(m => <option key={m.id} value={m.name} />)}
                      </datalist>
                      {form.model && !modelOptions.some(m => m.name?.toLowerCase() === form.model?.toLowerCase()) && (
                        <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setAddingModel(true)} style={{ flexShrink: 0, fontSize: '0.75rem' }}>+Catálogo</button>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="dv-input" value={newModel || form.model} onChange={e => setNewModel(e.target.value)} placeholder="Nombre modelo" />
                      <button type="button" className="dv-btn dv-btn-primary" onClick={saveModelToCatalog} style={{ flexShrink: 0 }}>Guardar</button>
                      <button type="button" className="dv-btn dv-btn-secondary" onClick={() => { setAddingModel(false); setNewModel(''); }} style={{ flexShrink: 0 }}>✕</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Tipo de dispositivo */}
              <div className="dv-section-title" style={{ marginTop: 20 }}>Tipo de dispositivo</div>
              <div className="dv-grid-2">
                <div className="dv-field">
                  <label className="dv-label">Tipo de cabina</label>
                  <select className="dv-select" name="storage_flavor" value={form.storage_flavor} onChange={handleChange}>
                    <option value="appliance">🏢 Cabina dedicada (appliance)</option>
                    <option value="server_based">🖥️ Servidor + Software (Linux, ZFS, Ceph…)</option>
                  </select>
                </div>
                {form.storage_flavor === 'server_based' && (
                  <div className="dv-field">
                    <label className="dv-label">SO / Distribución</label>
                    <input className="dv-input" name="os_distribution" value={form.os_distribution} onChange={handleChange} placeholder="Ubuntu 22.04 + ZFS, Ceph 18, TrueNAS…" />
                  </div>
                )}
              </div>

              {/* Protocolos */}
              <div className="dv-field" style={{ marginTop: 16 }}>
                <label className="dv-label">Protocolos soportados</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                  {PROTOCOLS.map(proto => {
                    const active = (form.protocols || []).includes(proto);
                    const pc = PROTOCOL_COLORS[proto] || { bg: '#f1f5f9', color: '#475569' };
                    return (
                      <button
                        key={proto}
                        type="button"
                        onClick={() => toggleProtocol(proto)}
                        style={{
                          padding: '5px 14px',
                          borderRadius: 20,
                          border: active ? `2px solid ${pc.color}` : '2px solid #e2e8f0',
                          background: active ? pc.bg : '#fff',
                          color: active ? pc.color : '#94a3b8',
                          fontWeight: active ? 700 : 400,
                          fontSize: 13,
                          cursor: 'pointer',
                          transition: 'all .12s',
                        }}
                      >
                        {active ? '✓ ' : ''}{proto}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ─── UBICACIÓN (ficha clásica; en tarjetas va en General) ─── */}
          {!isCards && activeTab === 'ubicacion' && (
            <div className={paneClass}>
            <div className="dv-section">
              <div className="dv-section-title">Ubicación física</div>
              <div className="dv-grid-2">
                <div className="dv-field">
                  <label className="dv-label">Factor de forma</label>
                  <select className="dv-select" name="form_factor" value={form.form_factor} onChange={handleChange}>
                    <option value="rack">Rack</option>
                    <option value="tower">Tower</option>
                    <option value="desktop">Desktop</option>
                  </select>
                </div>

                <div className="dv-field">
                  <label className="dv-label">Ubicación *</label>
                  <LocationTreeSelect
                    locations={locations}
                    value={form.location_id}
                    onChange={(id) => handleLocationChange({ target: { value: id } })}
                    placeholder="— selecciona ubicación —"
                  />
                </div>

                <div className="dv-field">
                  <label className="dv-label">Rack</label>
                  <select className="dv-select" value={form.rack_id} onChange={handleRackChange}>
                    <option value="">— sin rack —</option>
                    {filteredRacks.map(r => <option key={r.id} value={r.id}>{r.name || r.code}</option>)}
                  </select>
                </div>

                {form.rack_id && (
                  <>
                    <div className="dv-field">
                      <label className="dv-label">Tamaño (U)</label>
                      <input
                        className="dv-input"
                        name="size_u"
                        type="number"
                        min="1"
                        max={rackHeight || 50}
                        value={form.size_u}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="dv-field">
                      <label className="dv-label">Posición U {rackHeight ? `(1–${rackHeight})` : ''}</label>
                      <input
                        className="dv-input"
                        name="position_u"
                        type="number"
                        min="1"
                        max={rackHeight || undefined}
                        value={form.position_u}
                        onChange={handleChange}
                        placeholder="U top-of-element"
                      />
                      {rangeLabel && <p className="dv-hint">Ocupa {rangeLabel}</p>}
                    </div>
                  </>
                )}
              </div>
            </div>
            </div>
          )}

          {/* ─── HARDWARE ─── */}
          {activeTab === 'hardware' && (
            <div className={paneClass}>
            <div className="dv-section">
              <div className="dv-section-title">Hardware del host</div>
              <p className="dv-hint" style={{ marginBottom: 12 }}>
                Para cabinas dedicadas (appliance) estos campos son opcionales. Para servidores convertidos en storage son los datos del servidor físico.
              </p>
              <div className="dv-grid-2">
                <div className="dv-field">
                  <label className="dv-label">CPU</label>
                  <input className="dv-input" name="cpu" value={form.cpu} onChange={handleChange} placeholder="2× Intel Xeon Silver 4214R" />
                </div>
                <div className="dv-field">
                  <label className="dv-label">RAM (GB)</label>
                  <input className="dv-input" name="ram_gb" type="number" min="0" value={form.ram_gb} onChange={handleChange} />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ─── LUNS ─── */}
          {activeTab === 'luns' && isExisting && (
            <div className={paneClass}>
              <StorageLunsPanel
                elementId={storage.id}
                companyId={companyId}
                ports={cabinetPorts}
                onChanged={refreshLunCount}
              />
            </div>
          )}

          {/* ─── LICENCIA ─── */}
          {activeTab === 'licencia' && (
            <div className={paneClass}>
            <div className="dv-section">
              <div className="dv-section-title">Licencia y soporte</div>
              <div className="dv-grid-2">
                <div className="dv-field">
                  <label className="dv-label">Licencia / contrato</label>
                  <input className="dv-input" name="license" value={form.license} onChange={handleChange} placeholder="HPE iLO Essentials, Nutanix NCI…" />
                </div>
                <div className="dv-field">
                  <label className="dv-label">Estado licencia</label>
                  <select className="dv-select" name="license_status" value={form.license_status} onChange={handleChange}>
                    <option value="vigente">Vigente</option>
                    <option value="expirada">Expirada</option>
                    <option value="renovacion">En renovación</option>
                  </select>
                </div>
                <div className="dv-field">
                  <label className="dv-label">Vencimiento</label>
                  <input className="dv-input" name="license_expires_at" type="date" value={form.license_expires_at ? form.license_expires_at.substring(0, 10) : ''} onChange={handleChange} />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ─── ORGANIZACIÓN ─── */}
          {activeTab === 'organizacion' && (
            <div className={paneClass}>
            <div className="dv-section">
              <div className="dv-section-title">Organización y roles</div>
              <div className="dv-grid-2">
                <div className="dv-field">
                  <label className="dv-label">Equipo / propietario</label>
                  <input className="dv-input" name="owner" value={form.owner} onChange={handleChange} placeholder="Infraestructura, BackupTeam…" />
                </div>
                <div className="dv-field">
                  <label className="dv-label">Tags (separados por coma)</label>
                  <input className="dv-input" name="tags" value={form.tags} onChange={handleChange} placeholder="SAN, backup, produccion" />
                </div>
                <div className="dv-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="dv-label">Roles (Ctrl+click para selección múltiple)</label>
                  <select
                    className="dv-select"
                    multiple
                    size={Math.min(8, roleOptions.length + 1)}
                    value={(form.role_ids || []).map(String)}
                    onChange={handleRolesChange}
                  >
                    {roleOptions.map(r => (
                      <option key={r.id} value={String(r.id)}>{r.code || r.name}</option>
                    ))}
                  </select>
                  {selectedRoles.length > 0 && (
                    <p className="dv-hint">Seleccionados: {selectedRoles.map(r => r.code || r.name).join(', ')}</p>
                  )}
                </div>
              </div>
            </div>
            </div>
          )}

          {/* ─── RED / IP ─── */}
          {activeTab === 'red' && isExisting && (
            <div className={paneClass}>
              <ElementIpPanel
                objectType="rack_element"
                objectId={storage?.id}
                companyId={companyId}
                elementName={storage?.name || form?.name}
                hasHardwarePorts
                getStorageBlob={(id, cid) => api.getStorageArrayDiskInventory(id, cid)}
                onCountChange={setIpCount}
              />
              <NetworkConnectionsPanel
                objectType="rack_element"
                objectId={storage?.id}
                companyId={companyId}
                elementName={storage?.name || form?.name}
              />
            </div>
          )}
          {activeTab === 'red' && !isExisting && (
            <div className={`${paneClass} dv-section`}>
              <p className="dv-hint">Guarda la cabina primero para poder asignar IPs adicionales.</p>
            </div>
          )}
          {/* ─── ACCESOS ─── */}
          {activeTab === 'access' && (
            <div className={paneClass}>
              <div className="dv-section">
                <DeviceAccessPanel
                  objectType="rack_element"
                  objectId={storage?.id}
                  companyId={companyId}
                  elementName={storage?.name || form?.name}
                  defaultHost={form.ip_address}
                  onCountChange={setAccessCount}
                  canEdit={editing}
                />
              </div>
            </div>
          )}

          {/* ─── ALIMENTACIÓN ─── */}
          {activeTab === 'power' && isExisting && (
            <div className={paneClass}>
              <div className="dv-section">
                <PowerConnectionsPanel
                  consumerType="rack_element"
                  consumerId={storage.id}
                  companyId={companyId}
                  rackId={form.rack_id ? Number(form.rack_id) : undefined}
                  elementName={form.name}
                />
              </div>
            </div>
          )}
          {activeTab === 'power' && !isExisting && (
            <div className={`${paneClass} dv-section`}>
              <p className="dv-hint">Guarda la cabina primero para poder asignar conexiones de alimentación.</p>
            </div>
          )}

          {errors.length > 0 && (
            <div className="dv-errors">
              {errors.map((e, i) => <p key={i} className="dv-error-item">⚠ {e}</p>)}
            </div>
          )}
          </fieldset>

          <FormDirtyDock
            show={isCards && (isDirty || nameDirty)}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={commitTitle}
          />
          </FormCardsShell>
        </div>

        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="storage"
          ciId={storage?.id}
          tipoLegible="la cabina"
        />
      </div>

      {/* Diálogo: eliminar cabina (borrado FÍSICO: se lleva componentes,
          discos y LUNs publicados) */}
      {showDeleteConfirm && (
        <Dialog
          title="Eliminar cabina"
          onClose={() => setShowDeleteConfirm(false)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="list-btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Eliminando…' : 'Eliminar'}
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar la cabina <strong>{form.name || storage?.name}</strong>?{'\n'}
            Se eliminarán también sus componentes, sus discos y los LUNs que publica. No se puede deshacer.
          </p>
        </Dialog>
      )}
    </div>
  );
}

export default StorageArrayForm;
